// services/profileService.js
// P2-B — own-profile phone + A2P consent reads/writes (Spec D).
//
// Storage: the P2-A columns on public.user_profiles (phone, sms_consent,
// sms_consent_at, sms_consent_source). Access relies entirely on the EXISTING
// own-row RLS ("Users can read/update/upsert own profile") — no policy change
// shipped with this branch. Conflict target: the user_profiles_user_id_key
// unique index (verified in production before this was written).
//
// This module does NOT send SMS, does NOT touch requests.sms_consent (the
// per-booking transactional consent), and has no Stripe/checkout/schedule
// surface. sms_consent_source ∈ 'signup' | 'google_phone_capture'.
//
// Original IP — LoadDash Services.

import { supabase } from "./supabaseService";

/**
 * Fetch the signed-in user's own profile row (phone/consent slice).
 * Returns null when no row exists yet (fresh accounts) or on error —
 * callers treat null as "needs capture".
 */
export async function getOwnProfile(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("user_id, phone, sms_consent")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) return null;
    return data || null;
  } catch (e) {
    return null;
  }
}

/**
 * Record the user's phone and their EXPLICIT consent choice on their own
 * profile row. Partial upsert: only these columns are written; behavioral
 * score columns on the same row are untouched. When consent is false the
 * at/source fields are cleared — the row always reflects the latest explicit
 * choice the user made.
 *
 * @param {object} args
 * @param {string} args.userId
 * @param {string} args.phone
 * @param {boolean} args.smsConsent - checkbox state (unchecked by default in UI)
 * @param {'signup'|'google_phone_capture'} args.source
 */
export async function upsertOwnPhoneConsent({ userId, phone, smsConsent, source }) {
  if (!userId) return { error: new Error("upsertOwnPhoneConsent: missing userId") };
  const row = {
    user_id: userId,
    phone: (typeof phone === "string" && phone.trim()) ? phone.trim() : null,
    sms_consent: !!smsConsent,
    sms_consent_at: smsConsent ? new Date().toISOString() : null,
    sms_consent_source: smsConsent ? source : null,
  };
  return await supabase.from("user_profiles").upsert(row, { onConflict: "user_id" });
}
